// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { useEvolveGet } from '@evolvemep/api-ts';
import { Stack, Text, Title } from '@mantine/core';

import { MobileBasePageHeader } from 'components/Mantine/MobileBasePageHeader';
import { MobileLinkButtonGroup } from 'components/Mantine/MobileLinkButtonGroup';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import type { ShipmentDetailPageProps } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentDetailWrapper';

import { ShipmentItemsList } from '../ShipmentItemsList/ShipmentItemsList';
import { ShipmentWorkflowButtons } from '../ShipmentWorkflowButtons';

export const MobileShipmentDetailPage = ({ shipment, onShipmentUpdated }: ShipmentDetailPageProps) => {
  const navigate = useEvolveNavigate('', true);

  const { apiCall: getShipmentApiCall } = useEvolveGet(`shop/shipment/${shipment.shipmentId}`);
  const refreshShipment = useCallback(() => {
    void getShipmentApiCall().then(onShipmentUpdated);
  }, [getShipmentApiCall, onShipmentUpdated]);

  return (
    <Stack flex={1} mih={0} w="100%" p="md" pb={0} gap="xs">
      <MobileBasePageHeader
        title={shipment.identifier}
        parentPage={{ title: 'Shipping Orders', url: '/shipping-orders' }}
        rightSideComponent={
          <ShipmentWorkflowButtons
            shipment={shipment}
            onShipmentUpdated={refreshShipment}
            onShipmentDeleted={() => navigate('/shipping-orders')}
          />
        }
      />

      <Stack flex={1} style={{ overflowY: 'auto' }} gap="md">
        <Stack gap={0}>
          <Text c="dimmed" fz="sm">
            {shipment.identifier}
          </Text>
          <Title order={3}>{shipment.name}</Title>
        </Stack>

        {!!shipment.description && <Text style={{ whiteSpace: 'pre-wrap' }}>{shipment.description}</Text>}

        <MobileLinkButtonGroup
          from={shipment.identifier}
          linkButtons={[
            {
              statusBadgeProps: {
                customStatusEntityName: 'Shipment',
                stateName: shipment.shipmentStateName,
                statusId: shipment.shipmentStatusId,
              },
              date: shipment.shipByDate,
              assignees: shipment.assignees,
              to: `/shipping-orders/${shipment.shipmentId}/details`,
            },
            { label: 'Activity', to: `/shipping-orders/${shipment.shipmentId}/activity` },
          ]}
        />

        <ShipmentItemsList shipment={shipment} onItemsUpdated={refreshShipment} />
      </Stack>
    </Stack>
  );
};
